import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './LogoCarousel.css';

const LOGO_SLOT_WIDTH = 120; // Largeur de chaque "case" (logo + son padding interne)
const AUTO_SCROLL_INTERVAL = 1500; // Ton intervalle actuel
const MIN_SWIPE_THRESHOLD = 40; // Distance minimale en pixels pour considérer un swipe

function LogoCarousel({ logos }) {
  const [currentIndex, setCurrentIndex] = useState(0); // Index dans la liste étendue
  const trackRef = useRef(null);
  const autoScrollTimerRef = useRef(null);
  const isJumpingRef = useRef(false); // Pour gérer le saut sans transition

  // Références pour le défilement tactile
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);

  // Créer une liste étendue de logos pour la boucle
  const extendedLogos = useMemo(() => {
    if (!logos || logos.length === 0) return [];
    if (logos.length < 4) {
      let temp = [];
      for (let i = 0; i < 5; i++) temp.push(...logos);
      return temp;
    }
    return [...logos, ...logos, ...logos];
  }, [logos]);

  // Le point de départ dans la liste étendue
  const startingPointInExtendedList = useMemo(() => {
    if (!logos || logos.length === 0) return 0;
    if (logos.length < 4) return logos.length * 2;
    return logos.length;
  }, [logos]);

  // Initialiser currentIndex
  useEffect(() => {
    setCurrentIndex(startingPointInExtendedList);
  }, [startingPointInExtendedList]);

  // Fonctions pour avancer/reculer le carrousel
  const advanceCarousel = useCallback((direction) => {
    if (isJumpingRef.current) return;
    setCurrentIndex(prev => prev + direction);
  }, []);

  const handleNext = useCallback(() => advanceCarousel(1), [advanceCarousel]);
  const handlePrev = useCallback(() => advanceCarousel(-1), [advanceCarousel]);

  // Fonctions pour gérer le défilement automatique
  const stopAutoScroll = useCallback(() => {
    clearInterval(autoScrollTimerRef.current);
  }, []);

  const startAutoScroll = useCallback(() => {
    stopAutoScroll(); // S'assurer qu'il n'y a pas de timers multiples
    if (logos && logos.length > 0) {
      autoScrollTimerRef.current = setInterval(handleNext, AUTO_SCROLL_INTERVAL);
    }
  }, [logos, handleNext, stopAutoScroll]); // AUTO_SCROLL_INTERVAL est une const, pas besoin en dep

  // Défilement automatique (modifié pour utiliser start/stopAutoScroll)
  useEffect(() => {
    if (!logos || logos.length === 0) {
      stopAutoScroll();
      return;
    }
    startAutoScroll();
    return stopAutoScroll; // Nettoyage au démontage ou avant redémarrage
  }, [logos, startAutoScroll, stopAutoScroll]);

  // Gestionnaires d'événements tactiles
  const handleTouchStart = useCallback((e) => {
    stopAutoScroll();
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  }, [stopAutoScroll]);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartXRef.current || !touchStartYRef.current) {
      return;
    }
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = Math.abs(currentX - touchStartXRef.current);
    const deltaY = Math.abs(currentY - touchStartYRef.current);

    if (deltaX > deltaY) {
      e.preventDefault(); // Empêche le scroll vertical de la page si swipe horizontal
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartXRef.current) return;

    const touchEndX = e.changedTouches[0].clientX;
    const swipeDelta = touchStartXRef.current - touchEndX;

    if (Math.abs(swipeDelta) > MIN_SWIPE_THRESHOLD) {
      if (swipeDelta > 0) { // Swipe vers la gauche
        handleNext();
      } else { // Swipe vers la droite
        handlePrev();
      }
    }

    touchStartXRef.current = 0;
    touchStartYRef.current = 0;
    startAutoScroll(); // Redémarre l'auto-scroll après le swipe
  }, [handleNext, handlePrev, startAutoScroll]); // MIN_SWIPE_THRESHOLD est une const

  // Gestion du saut pour l'effet de boucle infinie (inchangé)
  useEffect(() => {
    const trackElement = trackRef.current;
    if (!trackElement || !logos || logos.length === 0) return;

    const handleTransitionEnd = () => {
      const L = logos.length;
      let newCalculatedIndex = currentIndex;
      let needsToJump = false;

      if (currentIndex >= startingPointInExtendedList + L) {
        newCalculatedIndex = startingPointInExtendedList + (currentIndex % L);
        needsToJump = true;
      } else if (currentIndex < startingPointInExtendedList) {
        let remainder = (currentIndex % L + L) % L;
        newCalculatedIndex = startingPointInExtendedList + remainder;
        needsToJump = true;
      }

      if (needsToJump) {
        isJumpingRef.current = true;
        trackElement.style.transition = 'none';
        setCurrentIndex(newCalculatedIndex);
        // eslint-disable-next-line no-void
        void trackElement.offsetHeight;
        setTimeout(() => {
          isJumpingRef.current = false;
          if (trackElement && !isJumpingRef.current) {
            trackElement.style.transition = 'transform 0.5s ease-in-out';
          }
        }, 50);
      }
    };

    trackElement.addEventListener('transitionend', handleTransitionEnd);
    return () => {
      trackElement.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [currentIndex, startingPointInExtendedList, logos]);

  if (!extendedLogos || extendedLogos.length === 0) {
    return <div className="logo-carousel-wrapper"><p>Aucun logo à afficher.</p></div>;
  }

  const trackStyle = {
    width: `${extendedLogos.length * LOGO_SLOT_WIDTH}px`,
    transform: `translateX(-${currentIndex * LOGO_SLOT_WIDTH}px)`,
    transition: isJumpingRef.current ? 'none' : 'transform 0.5s ease-in-out',
  };

  return (
    <div
      className="logo-carousel-wrapper"
      onMouseEnter={stopAutoScroll}    // Modifié
      onMouseLeave={startAutoScroll}   // Modifié
      onTouchStart={handleTouchStart}  // Nouveau
      onTouchMove={handleTouchMove}    // Nouveau
      onTouchEnd={handleTouchEnd}      // Nouveau
    >
      <button className="carousel-arrow prev-arrow" onClick={handlePrev} aria-label="Logos précédents">&#10094;</button>
      <div className="logo-carousel-viewport">
        <div
          ref={trackRef}
          className="logo-carousel-track"
          style={trackStyle}
        >
          {extendedLogos.map((logo, index) => (
            <div className="logo-slide" key={`${logo.id || index}-${index}`} style={{ width: `${LOGO_SLOT_WIDTH}px` }}>
              {/* J'ai ajouté logo.id || index pour la key au cas où id ne serait pas toujours défini */}
              <img src={logo.src} alt={logo.alt} />
            </div>
          ))}
        </div>
      </div>
      <button className="carousel-arrow next-arrow" onClick={handleNext} aria-label="Logos suivants">&#10095;</button>
    </div>
  );
}

export default LogoCarousel;